import * as React from "react";

const ROWS_PER_PAGE = 25;
const TOTAL_TEMPLATE_PHOTOS = 28;

function getPhotoCount(vehicle, photoSummary) {
    return photoSummary[vehicle.id]?.photo_count || 0;
}

function PhotoDashboard({ vehicles, photoSummary, loadingSummary, searchFilter, onSelectVehicle }) {
    const [sortField, setSortField] = React.useState("photo_count");
    const [sortDir, setSortDir] = React.useState("asc");
    const [page, setPage] = React.useState(0);

    // Reset page when filter changes
    React.useEffect(() => {
        setPage(0);
    }, [searchFilter]);

    // Filter vehicles by search input
    const filteredVehicles = React.useMemo(() => {
        if (!searchFilter || !searchFilter.trim()) return vehicles;
        const q = searchFilter.trim().toLowerCase();
        return vehicles.filter(v =>
            (v.stock && v.stock.toLowerCase().includes(q)) ||
            (v.make && v.make.toLowerCase().includes(q)) ||
            (v.model && v.model.toLowerCase().includes(q)) ||
            (v.year && String(v.year).includes(q))
        );
    }, [vehicles, searchFilter]);

    // Sort vehicles
    const sortedVehicles = React.useMemo(() => {
        const sorted = [...filteredVehicles].sort((a, b) => {
            let aVal, bVal;
            if (sortField === "photo_count") {
                aVal = getPhotoCount(a, photoSummary);
                bVal = getPhotoCount(b, photoSummary);
            } else if (sortField === "last_upload") {
                aVal = photoSummary[a.id]?.last_upload || "";
                bVal = photoSummary[b.id]?.last_upload || "";
            } else if (sortField === "stock") {
                aVal = a.stock || "";
                bVal = b.stock || "";
            } else if (sortField === "vehicle") {
                aVal = `${a.year} ${a.make} ${a.model}`;
                bVal = `${b.year} ${b.make} ${b.model}`;
            }
            if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [filteredVehicles, photoSummary, sortField, sortDir]);

    // Pagination
    const totalPages = Math.ceil(sortedVehicles.length / ROWS_PER_PAGE);
    const pageVehicles = sortedVehicles.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

    // Stats
    const stats = React.useMemo(() => {
        let withPhotos = 0;
        let withoutPhotos = 0;
        for (const v of vehicles) {
            if (getPhotoCount(v, photoSummary) > 0) {
                withPhotos++;
            } else {
                withoutPhotos++;
            }
        }
        return { total: vehicles.length, withPhotos, withoutPhotos };
    }, [vehicles, photoSummary]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("asc");
        }
        setPage(0);
    };

    const getSortIndicator = (field) => {
        if (sortField !== field) return "";
        return sortDir === "asc" ? " ▲" : " ▼";
    };

    if (loadingSummary) {
        return <div className="photo-dashboard">Loading photo summary...</div>;
    }

    return (
        <div className="photo-dashboard">
            <div className="photo-dashboard-header">
                <h2>Photo Inventory</h2>
            </div>

            <div className="photo-dashboard-stats">
                <div className="photo-dashboard-stat">
                    <span className="photo-dashboard-stat-number">{stats.total}</span>
                    <span className="photo-dashboard-stat-label">Total Vehicles</span>
                </div>
                <div className="photo-dashboard-stat photo-dashboard-stat-good">
                    <span className="photo-dashboard-stat-number">{stats.withPhotos}</span>
                    <span className="photo-dashboard-stat-label">With Photos</span>
                </div>
                <div className="photo-dashboard-stat photo-dashboard-stat-none">
                    <span className="photo-dashboard-stat-number">{stats.withoutPhotos}</span>
                    <span className="photo-dashboard-stat-label">No Photos</span>
                </div>
            </div>

            {filteredVehicles.length === 0 ? (
                <div className="photo-dashboard-empty">
                    No vehicles found{searchFilter ? ` matching "${searchFilter}"` : ""}.
                </div>
            ) : (
                <>
                    <div className="photo-dashboard-table-wrapper">
                        <table className="photo-dashboard-table">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort("stock")}>
                                        Stock #{getSortIndicator("stock")}
                                    </th>
                                    <th onClick={() => handleSort("vehicle")}>
                                        Vehicle{getSortIndicator("vehicle")}
                                    </th>
                                    <th onClick={() => handleSort("photo_count")}>
                                        Photos{getSortIndicator("photo_count")}
                                    </th>
                                    <th className="photo-dashboard-col-date" onClick={() => handleSort("last_upload")}>
                                        Last Upload{getSortIndicator("last_upload")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageVehicles.map((v) => {
                                    const count = getPhotoCount(v, photoSummary);
                                    const pct = Math.min((count / TOTAL_TEMPLATE_PHOTOS) * 100, 100);
                                    const summary = photoSummary[v.id] || {};
                                    const lastUpload = summary.last_upload
                                        ? new Date(summary.last_upload).toLocaleDateString()
                                        : null;

                                    return (
                                        <tr key={v.id} onClick={() => onSelectVehicle(v)}>
                                            <td className="photo-dashboard-stock">{v.stock}</td>
                                            <td>{v.year} {v.make} {v.model}</td>
                                            <td>
                                                <div className="photo-dashboard-photo-cell">
                                                    <span>{count} / {TOTAL_TEMPLATE_PHOTOS}</span>
                                                    <div className="photo-dashboard-progress">
                                                        <div
                                                            className="photo-dashboard-progress-fill"
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="photo-dashboard-col-date">
                                                {lastUpload || <span className="photo-dashboard-no-photos">No photos</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="photo-dashboard-pagination">
                            <button
                                disabled={page === 0}
                                onClick={() => setPage(p => p - 1)}
                            >
                                Previous
                            </button>
                            <span>Page {page + 1} of {totalPages}</span>
                            <button
                                disabled={page >= totalPages - 1}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default PhotoDashboard;
